import * as three from './three.bundle.js';
import { LitElement, html, css, nothing } from "./lit-core.min.js";

class Loader {
    constructor() {
        this.queue = [];
        this.receivedLength = 0;
        this.totalLength = 0;
        this.onProgress = null;
    }

    add(url, callback) {
        this.queue.push({ url, callback });
    }

    async load() {
        const responses = await Promise.all(this.queue.map(({ url, callback }) => fetch(url).then(response => ({ response, callback }))));
        
        for (const resp of responses) {
            this.totalLength += parseInt(resp.response.headers.get('content-length'));
        }

        await Promise.all(responses.map(resp => this.readResponse(resp.response, resp.callback)));
    }

    async readResponse(response, callback) {
        let receivedLength = 0;
        let chunks = [];
        const reader = response.body.getReader();

        while(true) {
            const {done, value} = await reader.read();
            
            if (done) {
                break;
            }
            
            chunks.push(value);

            receivedLength += value.length;
            this.receivedLength += value.length;
            if (typeof this.onProgress === 'function') this.onProgress(this.receivedLength, this.totalLength);
        }

        let data = new Uint8Array(receivedLength);
        let position = 0;

        for (const chunk of chunks) {
            data.set(chunk, position);
            position += chunk.length;
        }

        if (callback) await callback(data, response.headers.get('content-type'));
    }
}

class ByobRender extends HTMLElement {
    static get observedAttributes() { return ['render-config']; }

    constructor() {
        super();

        const observer = new IntersectionObserver((entries) => {
            if (entries.some(e => e.isIntersecting)) {
                this.init();
                observer.disconnect();
            }
        });

        this.coloursRequest = fetch('/?view=bundle-colours');

        observer.observe(this);

        // Start loading data when both bundle builder and hero image have loaded
        const bundlePromise = new Promise(resolve => {
            if (window.bundleBuilderReady) resolve();
            else window.addEventListener('bundlebuilderready', resolve);
        });

        const heroPromise = new Promise(resolve => {
            const heroImage = document.querySelector('.bundle-hero__image img');
            if (!heroImage || heroImage.complete) resolve();
            else heroImage.addEventListener('load', resolve);     
        });

        Promise.all([bundlePromise, heroPromise]).then(() => this.loadData());
    }

    async init() {
        const start = performance.now();

        if (this.initialised) return;
        this.initialised = true;
        console.log('Init render');
        three.ColorManagement.legacyMode = false;
        await this.loadData();
        this.setupScene();
        this.setupRenderer();  

        this.loadConfig(this.config);
        this.ready = true;

        window.FS('trackEvent', {
			name: 'BYOB Render Loaded',
			properties: { time: (performance.now() - start) / 1000 }
		});
    }

    setupScene() {
        this.scene = new three.Scene();
        this.scene.position.setY(-0.35);

        // Add bed
        this.scene.add(this.bed);

        this.map = this.textures[window.bundleUrls.map];
        this.bumpMap = this.textures[window.bundleUrls.bumpMap];
        let wood = this.textures[window.bundleUrls.wood];

        // Slightly scale up cotton duvet
        this.bed.getObjectByName("duvet-cover_1_1_cotton").scale.setScalar(1.01)


        // Set colour maps
        this.maps = {};
        for (let colour of this.bundleColours) {
            if (colour.texture) {
                this.maps[colour.id] = this.textures[colour.texture];
            }
        }

        // Create materials
        this.materials = {};
        this.materialsDoubleSided = {};
        this.materialsQuilt = {};
        this.materialsFrayed = {};
        this.materialsCotton = {};

        const frayedTextures = [];

        for (const textureUrl of frayedTextures) {
            this.textures[textureUrl].wrapS = three.RepeatWrapping;
            this.textures[textureUrl].wrapT = three.RepeatWrapping;
            this.textures[textureUrl].repeat.set(1, 1);
            this.textures[textureUrl].flipY = false;
        }

        for (let key in this.colours) {
            let baseColour = new three.Color(this.colours[key]);
            baseColour.name = key;

            // Set default texture map, or use map for specific colour if it exists
            let selectedMap = this.map;
            if (this.maps[key]) selectedMap = this.maps[key];

            this.materials[key] = this.createColourMaterial(baseColour, selectedMap);
            this.materialsCotton[key] = this.createColourMaterial(baseColour, selectedMap, false, { map: null, bumpMap: null, sheen: 0.09 });
            this.materialsDoubleSided[key] = this.createColourMaterial(baseColour, selectedMap, true);
            this.materialsQuilt[key] = this.createColourMaterial(baseColour, selectedMap, false, { normalMap: this.textures[window.bundleUrls.quiltNormalMap] });

            for (const otherKey in this.colours) {
                if (key.includes('stripe') || otherKey.includes('stripe')) continue;

                let otherBaseColour = new three.Color(this.colours[otherKey]);
                let combinedBaseColour = baseColour.clone().lerp(otherBaseColour, 0.5);

                if (frayedTextures.length > 0) {
                    const frayedKey = `${key}-${otherKey}`;

                    this.materialsFrayed[frayedKey] = this.createColourMaterial(combinedBaseColour, selectedMap, false, {
                        normalMap: this.textures[window.bundleUrls.frayedNormal],
                        alphaMap: this.textures[window.bundleUrls.frayedOpacity],
                        bumpMap: null,
                        transparent: true,
                    })
                }
            }
        };

        // Set default material for bedding items
        for (let child of this.bed.children) {
            child.material = this.materials.white;
            child.castShadow = true; //default is false
            child.receiveShadow = true; //default  

            // Scale up flat sheet slightly to avoid z-fighting
            if (child.name.includes('flat-sheet')) {
                child.scale.setScalar(1.01);
            }
        }

        // Set base material
        const baseMat = new three.MeshStandardMaterial({
            map: wood,
            color: new three.Color()
        });   
        baseMat.color.setHSL(0.083, 0.17, 0.4);
        
        let base = this.scene.getObjectByName("base");
        base.material = baseMat;


        // Add shadow
        const shadowPlane = new three.PlaneGeometry( 5, 5 );
        const shadowMat = new three.MeshBasicMaterial({
            color: 0x000000,
            transparent: true,
            opacity: 0.85,
            alphaMap: this.textures[window.bundleUrls.duvetLinenShadowAlpha],
        });
        const shadow = new three.Mesh( shadowPlane, shadowMat );
        shadow.rotateX(-Math.PI / 2);
        this.scene.add( shadow );
        this.shadow = shadow;
        this.shadow.duvetLinenAlpha = this.textures[window.bundleUrls.duvetLinenShadowAlpha];
        this.shadow.duvetCottonAlpha = this.textures[window.bundleUrls.duvetCottonShadowAlpha];
        this.shadow.quiltAlpha = this.textures[window.bundleUrls.quiltShadowAlpha];
    }

    async loadData() {
        if (this.dataPromise) return this.dataPromise;
        console.log('Load data');
        this.dataPromise = this.fetchData();
        return this.dataPromise;
    }

    async fetchData() {
        // Fetch colours
        const coloursResp = await this.coloursRequest;
        this.bundleColours = await coloursResp.json();
        this.colours = Object.fromEntries(this.bundleColours.map(c => [c.id, c.hex]));

        this.colours = {
            ...this.colours,
            "aegean": "#14465C",
            "apple": "#92C28E",
            "butterscotch": "#E19270",
            "moss": "#042B03",
            "espresso": "#4A2417",
            "pink-clay": "#BD6565",
            "oatmeal-white-stripe": "#E2D8C5",
            "sage": "#ACBD9E",
            "forest": "#003D36",
            "olive-stripe": "#434E2C",
        }

        // Load textures
        const textureLoader = new three.TextureLoader();

        const textureUrls = [
            window.bundleUrls.map,
            window.bundleUrls.bumpMap,
            window.bundleUrls.wood,
            window.bundleUrls.quiltNormalMap,
            window.bundleUrls.duvetCottonShadowAlpha,
            window.bundleUrls.duvetLinenShadowAlpha,
            window.bundleUrls.quiltShadowAlpha,
            ...this.bundleColours.filter(colour => colour.texture).map(colour => colour.texture)
        ];

        const urlLoader = new Loader();
        urlLoader.onProgress = (received, total) => this.style.setProperty('--progress', (received / total).toFixed(2));

        this.textures = {};

        for (const url of textureUrls) {
            urlLoader.add(url, (data, type) => {
                const blob = new Blob([data], { type });
                const dataUrl = URL.createObjectURL(blob);
                this.textures[url] = textureLoader.load(dataUrl);
            });
        }

        // Load env map (new mode)
        const envLoader = new three.RGBELoader();
        urlLoader.add(window.bundleUrls.envMap, (data, type) => {
            const blob = new Blob([data], { type });
            const dataUrl = URL.createObjectURL(blob);
            this.envTexture = envLoader.load(dataUrl);
        });

        // Load model
        const loader = new three.GLTFLoader();
        const dracoLoader = new three.DRACOLoader();
        dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
        loader.setDRACOLoader(dracoLoader);

        urlLoader.add(window.bundleUrls.model, async (data, type) => {
            let model = await loader.parseAsync(data.buffer);
            this.bed = model.scene;
        });

        this.classList.add('loading');
        await urlLoader.load();
        this.classList.add('loaded'); 
    }

    createColourMaterial(baseColour, map, doubleSided = false, props = {}) {
        let materialConfig = {
            bumpScale: 15,
            map: map,
            bumpMap: this.bumpMap,
            roughness: 1,
            metalness: 0,
            color: baseColour,
            sheenColor: new three.Color(0xffffff),
            sheen: 0.05,
            side: doubleSided ? three.DoubleSide : three.FrontSide,
            normalScale: new three.Vector2(2, 2),
            ...props
        };

        return new three.MeshPhysicalMaterial(materialConfig);
    }

    setupRenderer() {
        this.renderer = new three.WebGLRenderer({antialias: true, preserveDrawingBuffer: true});
        this.renderer.outputColorSpace = three.SRGBColorSpace;
        this.renderer.toneMapping = three.ACESFilmicToneMapping;
        this.renderer.shadowMap.autoUpdate = false;
        this.renderer.shadowMap.needsUpdate = true;
        this.renderer.shadowMap.type = three.PCFSoftShadowMap;
        this.renderer.setClearColor(0xffffff, 1);
        this.renderer.toneMappingExposure = 1;
        
        this.renderer.shadowMap.enabled = true;

        let root = this.attachShadow({mode: 'closed'});
        root.appendChild(this.renderer.domElement);
        this.renderer.domElement.style.opacity = '0';

        this.renderer.domElement.insertAdjacentHTML('beforebegin', /* html */`
            <style>
                canvas {
                    aspect-ratio: 1;
                    max-width: 100%;
                    max-height: var(--max-height, 100%);
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                    transform: scale(var(--zoom, 1));
                    transition: transform 300ms, opacity 350ms;
                }
            </style>
        `);

        let camera = new three.PerspectiveCamera( 30, 1.5, 0.1, 1000 );
        camera.position.z = 4;
        camera.position.y = 1.75;
        this.camera = camera;

        this.composer = new three.EffectComposer(this.renderer);

        this.renderPass = new three.RenderPass(this.scene, this.camera);
        this.composer.addPass(this.renderPass);

        // Add ambient occlusion pass
        this.gtaoPass = new three.GTAOPass(this.scene, this.camera, undefined, undefined, undefined, {
            radius: 1,
            samples: 16,
            screenSpaceRadius: true,
        });
        this.composer.addPass(this.gtaoPass);
        this.gtaoPass.blendIntensity = 0.9;

        const outputPass = new three.OutputPass();
        this.composer.addPass(outputPass);

        // Add FXAA pass
        this.fxaaPass = new three.FXAAPass();
        this.composer.addPass(this.fxaaPass);

        // Set environment map
        const pmremGenerator = new three.PMREMGenerator( this.renderer );
        const envMap = pmremGenerator.fromEquirectangular(this.envTexture).texture;
        this.scene.environment = envMap;
        this.scene.environmentRotation = new three.Euler(0, 0.7, 0.4);
        const backgroundColour = new three.Color();
        backgroundColour.setRGB(2.3, 2.3, 2.3);
        this.scene.background = backgroundColour;

        // Setup controls - allows rotation and zoom using mouse or touch
        const controls = new three.OrbitControls(camera, this.renderer.domElement);
        controls.autoRotate = this.dataset.colourPicker !== 'true';
        controls.autoRotateSpeed = 0.31;
        controls.enableZoom = true;
        controls.enableDamping = true;
        controls.enablePan = false;
        controls.minDistance = 2.5;
        controls.maxDistance = Math.sqrt(Math.pow(4, 2) + Math.pow(1.75, 2)); // Restrict to default distance
        controls.maxPolarAngle = Math.PI / 2;
        this.controls = controls;

        let clickStartX = null;
        let clickStartY = null;
        let animHandle = null;

        this.setAttribute('paused', false);
    
        this.addEventListener('mousedown', e => {
            clickStartX = e.offsetX;
            clickStartY = e.offsetY;
        })

        this.addEventListener('mouseup', e => {
            if (this.dataset.colourPicker === 'true') return;
            if (clickStartX == null || clickStartY == null) return; 
            const dist = Math.sqrt(Math.pow(e.offsetX - clickStartX, 2) + Math.pow(e.offsetY - clickStartY, 2));
            if (dist < 1) {
                if (animHandle != null) clearTimeout(animHandle);
                this.classList.remove('pause-animation');
                controls.autoRotate = !controls.autoRotate;
                this.setAttribute('paused', !controls.autoRotate);
                this.classList.add('pause-animation');
                animHandle = setTimeout(() => this.classList.remove('pause-animation'), 450);
            }
            clickStartX = null;
            clickStartY = null;
        });

        const playAnimOnLoad = () => {
            if (document.documentElement.classList.contains('modal-open') || !document.body.classList.contains('bundle-started')) setTimeout(playAnimOnLoad, 100);
            else {
                setTimeout(() => {
                    if (animHandle != null) clearTimeout(animHandle);
                    this.classList.add('pause-animation');
                    animHandle = setTimeout(() => this.classList.remove('pause-animation'), 450);
                }, 100);   
            }
        }

        playAnimOnLoad();

        // Add colour picker if enabled, and disable rotation
        if (this.dataset.colourPicker === "true") {
            // Generate predefined colours dropdown options, sorted alphabetically
            const sortedColourKeys = Object.keys(this.colours).sort();
            const colourOptions = sortedColourKeys.map(key => 
                `<option value="${key}">${key.charAt(0).toUpperCase() + key.slice(1).replace(/-/g, ' ')}</option>`
            ).join('');

            const picker = `
            <style>
            .colour-picker {
                margin-bottom: 8px;
                display: flex;
                flex-wrap: wrap;
                gap: 8px;
                z-index: 5;
                padding: 8px;
                background: white;
            }

            .colour-picker label {
                font-weight: 400;
            }

            .colour-group {
                display: flex;
                align-items: center;
                gap: 4px;
            }
            </style>
            <div class="colour-picker">
                <div class="colour-group">
                    <label for="predefined-colour">Predefined</label>
                    <select name="predefined-colour" id="predefined-colour">
                        <option value="">Select a colour...</option>
                        ${colourOptions}
                    </select>
                </div>
                <div class="colour-group">
                    <label>Custom</label>
                    <input type="color" value="${this.colours.white}">
                    <input type="text" value="${this.colours.white}">
                </div>
                <div class="colour-group">
                    <label for="fabric">Fabric</label>
                    <select name="fabric" id="fabric">
                        <option value="linen">Linen</option>
                        <option value="cotton">Cotton</option>
                    </select>
                </div>
                <div class="colour-group">
                    <label for="stripe">Stripe</label>
                    <select name="stripe" id="stripe">
                        <option value="none">None/Custom</option>
                        <option value="stripe">Stripe</option>
                        <option value="pinstripe">Pinstripe</option>
                        <option value="olive-stripe">Olive Stripe</option>
                        <option value="sage-olive-stripe">Sage & Olive Stripe</option>
                    </select>
                </div>
                <div class="colour-group">
                    <input type="file" name="texture" id="texture" accepts="image/jpeg, image/png">
                </div>
            </div>
            `;
            controls.autoRotate = false;
            root.querySelector('canvas').insertAdjacentHTML('beforebegin', picker);
            this.style.setProperty('--zoom', 1.5);
            let colour = this.colours.white;
            let stripe = 'none';
            let fabric = 'linen';

            root.querySelector('.colour-picker select[name="predefined-colour"]').addEventListener('change', e => {
                const selectedColourKey = e.currentTarget.value;
                if (selectedColourKey && this.colours[selectedColourKey]) {
                    colour = this.colours[selectedColourKey];
                    this.forceColour(colour, stripe, fabric);
                    root.querySelector('.colour-picker input[type="color"]').value = colour;
                    root.querySelector('.colour-picker input[type="text"]').value = colour;
                }
            });

            root.querySelector('.colour-picker input[type="color"]').addEventListener('input', e => {
                colour = e.currentTarget.value;
                this.forceColour(colour, stripe, fabric);
                root.querySelector('.colour-picker input[type="text"]').value = e.currentTarget.value;
                // Reset predefined dropdown when custom color is used
                root.querySelector('.colour-picker select[name="predefined-colour"]').value = '';
            });

            root.querySelector('.colour-picker input[type="color"]').addEventListener('change', e => {
                colour = e.currentTarget.value;
                this.forceColour(colour, stripe, fabric);
                root.querySelector('.colour-picker input[type="text"]').value = e.currentTarget.value;
                // Reset predefined dropdown when custom color is used
                root.querySelector('.colour-picker select[name="predefined-colour"]').value = '';
            });

            root.querySelector('.colour-picker input[type="text"]').addEventListener('input', e => {
                colour = e.currentTarget.value;
                this.forceColour(colour, stripe, fabric);
                root.querySelector('.colour-picker input[type="color"]').value = e.currentTarget.value;
                // Reset predefined dropdown when custom color is used
                root.querySelector('.colour-picker select[name="predefined-colour"]').value = '';
            });

            root.querySelector('.colour-picker select[name="fabric"]').addEventListener('change', e => {
                fabric = e.currentTarget.value;
                this.updateConfigFabric(fabric);
                this.forceColour(colour, stripe, fabric);
            });

            root.querySelector('.colour-picker select[name="stripe"]').addEventListener('change', e => {
                stripe = e.currentTarget.value;
                this.forceColour(colour, stripe, fabric);
            });
            
            root.querySelector('.colour-picker input[type="file"]').addEventListener('change', e => {
                let file = e.currentTarget.files[0];
                let reader = new FileReader(file);
                reader.addEventListener('load', e => {
                    let texture = new three.TextureLoader().load(e.target.result) 
                    texture.repeat = new three.Vector2(0.5, 0.5);
                    stripe = file.name;
                    this.maps[file.name] = texture;
                    this.forceColour(colour, stripe, fabric);
                })
                reader.readAsDataURL(file);
            });
        }

        // Progressive shadows will be loaded dynamically when needed
        this.progressiveShadows = null;

        // Update render size when element size changes
        const updateRenderSize = () => {
            const rect = this.renderer.domElement.getBoundingClientRect();
            const size = Math.min(rect.width, rect.height);
            const ratio = 1.75;
            const renderSize = size * ratio;

            this.renderer.setSize(renderSize, renderSize, false);
            this.composer.setSize(renderSize, renderSize);

            const scissorWidth = rect.width * ratio / 1.4;
            const scissorHeight = rect.height * ratio / 1.4;
            this.renderer.setScissor((renderSize - scissorWidth) / 2, (renderSize - scissorHeight) / 2, scissorWidth, scissorHeight);
            this.renderer.setScissorTest(true);
            camera.aspect = 1;
            camera.zoom = 0.6;
            camera.updateProjectionMatrix();
        };
        this.updateRenderSize = updateRenderSize;

        // Improve resize performance by waiting until resizing is finished
        let handler = null;
        const observer = new ResizeObserver(() => {
            if (handler) clearTimeout(handler);
            handler = setTimeout(() => {
                updateRenderSize();
                clearTimeout(handler);
            }, 100);
        });
        observer.observe(this);

        updateRenderSize();

        let lastTime = null;
        const frameTimes = [];

        // Setup update loop
        const animate = timestamp => {
            requestAnimationFrame(animate); 
            controls.update(); 
            if (controls.getAzimuthalAngle() >= controls.maxAzimuthAngle - 0.05) controls.autoRotateSpeed = Math.abs(controls.autoRotateSpeed);
            if (controls.getAzimuthalAngle() <= controls.minAzimuthAngle + 0.05) controls.autoRotateSpeed = -Math.abs(controls.autoRotateSpeed);

            this.composer.render();
            
            if (this.renderer.domElement.style.opacity == '0') {
                this.renderer.domElement.style.opacity = '1';
            }

            if (lastTime != null && frameTimes.length < 300) {
                const delta = timestamp - lastTime;
                frameTimes.push(delta);
                if (frameTimes.length == 300) {
                    window.FS('trackEvent', {
                        name: 'BYOB Render Performance',
                        properties: { 
                            frameTime: frameTimes.slice(200).reduce((a, b) => a + b) / 100,
                            legacy: false
                        }
                    });
                }
            }
            lastTime = timestamp;
        };

        animate();
    }

    createScreenshot(width = 400, height = undefined, { config = null, cameraDist = 1, position = null } = {}) {
        let aspect = width / (height ? height : width * 1.25);

        let camera = new three.PerspectiveCamera( 30, aspect, 0.1, 1000 );
        camera.position.x = (position?.x ?? 0) * cameraDist;
        camera.position.z = (position?.z ?? 5) * cameraDist;
        camera.position.y = (position?.y ?? 2.5) * cameraDist;
        camera.lookAt(new three.Vector3(0, 0, 0));

        if (config) this.loadConfig(config,  { updateShadows: false, fireEvent: false });

        let origSize = this.renderer.getSize(new three.Vector2());
    
        this.renderer.setScissorTest(false);
        this.renderer.setSize(width, height ? height : width * 1.25, false);
        this.composer.setSize(width, height ? height : width * 1.25);
        this.renderPass.camera = camera;
        this.gtaoPass.camera = camera;

        camera.updateProjectionMatrix();

        if (this.floorMaterial) {
            this.floorMaterial.camera = camera;
            this.floorMaterial.update();
        }

        this.composer.render();

        let canvas = this.renderer.domElement;
        let image = canvas.toDataURL('image/jpeg', 80);

        if (this.floorMaterial) this.floorMaterial.camera = this.camera;
       
        this.renderer.setScissorTest(true);
        this.renderer.setSize(origSize.width, origSize.height, false);
        this.composer.setSize(origSize.width, origSize.height);
        this.renderPass.camera = this.camera;
        this.gtaoPass.camera = this.camera;
        
        if (config) this.loadConfig(this.config, { updateShadows: false, fireEvent: false });

        return image;
    }

    updateConfig(variants, { updateShadows = true, fireEvent = true } = {}) {
        const firstCover = variants.find(v => v.modelId.includes('duvet') || v.modelId.includes('quilt'));
        const coverType = firstCover.modelId.includes('quilt') ? 'quilt' : 'duvet';
        const coverFabric = firstCover.fabric.toLowerCase();

        for (let child of this.bed.children) {
            if (child.name != 'base' && child.name != 'bed-head') child.visible = false;
        }

        // Add spacing when scalloped standards are present
        const hasScallopedStandard = variants.some(v => v.modelId.includes('standard') && v.colour.includes('scalloped'));
        const standards = this.bed.children.filter(c => c.name.includes('standard'));

        if (hasScallopedStandard) {
            for (const standard of standards) {
                if (!standard._scallopedTransform || standard._scallopedTransform == 0) {
                    standard._scallopedTransform = standard.name.endsWith('a') ? -0.03 : 0.03;
                    standard.translateX(standard._scallopedTransform);
                }
            }
        }
        else {
            for (const standard of standards) {
                if (standard._scallopedTransform) {
                    standard.translateX(-standard._scallopedTransform);
                    standard._scallopedTransform = 0;
                }
            }
        }

        // Vertical adjustment when there only a single standard pillow
        const standardSelections = variants.filter(v => v.modelId.includes('standard'));
        const pillowSelections = variants.filter(v => v.modelId.includes('pillowcase'));

        if (standardSelections.length == 1 && pillowSelections.length == 1) {
            for (const standard of standards) {
                if (!standard._singleStandardTransform || standard._singleStandardTransform == 0) {
                    standard._singleStandardTransform = 0.12;
                    standard.translateY(standard._singleStandardTransform);
                }
            }
        }
        else {
            for (const standard of standards) {
                if (standard._singleStandardTransform) {
                    standard.translateY(-standard._singleStandardTransform);
                    standard._singleStandardTransform = 0;
                }
            }
        }

        for (let { modelId, colour, flipped, fabric } of variants) {
            const baseMaterials = fabric == 'Cotton' ? this.materialsCotton : this.materials;

            if (modelId == 'flat-sheet_1_1') modelId = `flat-sheet_1_1_${coverType}_${coverFabric}`
            if (modelId == 'flat-sheet_1_2') modelId = `flat-sheet_1_2_${coverType}_${coverFabric}`

            if (modelId == 'duvet-cover_1_1') modelId = `duvet-cover_1_1_${fabric.toLowerCase()}`

            if (modelId == 'quilt') {
                let front = this.scene.getObjectByName('quilt_front');
                let back = this.scene.getObjectByName('quilt_back');

                front.visible = true;
                back.visible = true;

                if (colour.includes('-')) {
                    let split = colour.split('-');
                    let backSelection = split.pop();
                    let frontSelection = split.join('-');

                    front.material = this.materialsQuilt[flipped ? backSelection : frontSelection];
                    back.material = this.materialsQuilt[flipped ? frontSelection : backSelection];
                }
                else {
                    front.material = this.materialsQuilt[colour];
                    back.material = this.materials[colour];
                }
            }
            else if (modelId.includes('throw')) {
                const top = this.scene.getObjectByName(`throw-top_${coverType}`);
                const bottom = this.scene.getObjectByName(`throw-bottom_${coverType}`);

                top.visible = true;
                bottom.visible = true;

                top.material = this.materials[colour];
                bottom.material = this.materials[colour];

                if (colour.includes('-')) {
                    let split = colour.split('-');
                    let bottomSelection = split.pop();
                    let topSelection = split.join('-');

                    top.material = this.materials[flipped ? topSelection : bottomSelection];
                    bottom.material = this.materials[flipped ? bottomSelection : topSelection];
                }

                if (modelId.includes('muslin')) {
                    const topEdge = this.scene.getObjectByName(`throw-top-edge_${coverType}`);
                    const bottomEdge = this.scene.getObjectByName(`throw-bottom-edge_${coverType}`);
                   
                    topEdge.visible = true;
                    bottomEdge.visible = true;

                    topEdge.material = this.materialsFrayed[`${colour}-${colour}`];
                    bottomEdge.material = this.materialsFrayed[`${colour}-${colour}`];

                    if (colour.includes('-')) {
                        let split = colour.split('-');
                        let bottomSelection = split.pop();
                        let topSelection = split.join('-');

                        topEdge.material = this.materialsFrayed[`${topSelection}-${bottomSelection}`];
                        bottomEdge.material = this.materialsFrayed[`${topSelection}-${bottomSelection}`];
                    }
                }
            }
            else {
                const suffixes = modelId.includes('standard') ? ['a', 'b'] : [''];
                if (modelId == 'standard-pillowcase_1_1_0') modelId = `standard-pillowcase_1_2_0`;

                for (const suffix of suffixes) {
                    const objectId = `${modelId}${suffix}`;
                    let child = this.scene.getObjectByName(objectId);
                    if (!child) {
                        console.warn(`Could not find object with name ${objectId}`);
                        continue;
                    }
                    child.visible = true;

                    if (colour.includes('scalloped')) {
                        const type = objectId.includes('euro') ? 'euro' : 'standard';

                        let split = colour.split('-scalloped')[0].split('-');
                        let outerSelection = split.pop();
                        let innerSelection = split.join('-');
                        let id = objectId.split('_').slice(1).join('_');

                        let inner = this.scene.getObjectByName(`scalloped-${type}-inner_${id}`);
                        let outer = this.scene.getObjectByName(`scalloped-${type}-outer_${id}`);

                        inner.visible = true;
                        outer.visible = true;
                        if (baseMaterials[innerSelection]) {
                            child.material = baseMaterials[innerSelection];
                            inner.material = this.materialsDoubleSided[innerSelection];
                        }
                        if (baseMaterials[outerSelection]) outer.material = this.materialsDoubleSided[outerSelection];
                    }
                    else {
                        if (baseMaterials[colour]) child.material = baseMaterials[colour];
                    }
                }
            }
        }

        this.renderer.shadowMap.needsUpdate = true;

        // Dispatch render update event
        if (this.renderer && this.dataset.events == 'true' && fireEvent) {
            setTimeout(() => {
                this.dispatchEvent(new CustomEvent('renderupdate', { 
                    detail: { 
                        image: this.createScreenshot(1000), 
                        thumb: this.createScreenshot(400) 
                    }, 
                    bubbles: true })
                );
            }, 100);
        }
    }

    updateConfigFabric(fabric) {
        if (!this.config || !this.config.variants) return;
        
        // Capitalize fabric name to match config format
        const fabricName = fabric.charAt(0).toUpperCase() + fabric.slice(1);
        
        // Update all variants to use the selected fabric
        for (let variant of this.config.variants) {
            variant.fabric = fabricName;
        }
        
        // Reload the config to apply the fabric changes
        this.loadConfig(this.config);
    }

    forceColour(hex, stripe = 'none', fabric = 'linen') {
        let baseColour = new three.Color(hex);
        let map = this.map;

        if (stripe && stripe !== 'none') map = this.maps[stripe];

        // Create material based on fabric type
        let materialProps = {};
        if (fabric === 'cotton') {
            materialProps = { map: null, bumpMap: null };
        }

        // Always recreate the material to ensure proper fabric switching
        this.forceMaterial = this.createColourMaterial(baseColour, map, false, materialProps);

        for (let child of this.bed.children) {
            if (child.name == 'base') continue;
            child.material = this.forceMaterial;
        }
    }

    loadConfig(config,  { updateShadows = true, fireEvent = true } = {}) {
        const handlise = (string) => {
            return string.toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "").replace(/[^a-z0-9]+/g, '-');
        }

        let variants = config.variants;
        variants = variants.map(v => ({ type: handlise(v.type), colour: handlise(v.colour), flipped: v.flipped, fabric: v.fabric }));

        const types = {};
        
        // No king pillow model, so replace any with standard
        for (const variant of variants) {
            if (variant.type == 'king-pillowcase') variant.type = 'standard-pillowcase';
        }
        
        // Group variants by type, and find index of each variant (within selected variants of same type)
        for (const variant of variants) {
            if (!types[variant.type]) types[variant.type] = variants.filter(v => v.type == variant.type);
            variant.index = types[variant.type].indexOf(variant) + 1;
        }

        // Set model IDs
        for (const variant of variants) {
            if (variant.type == 'throw') variant.modelId = 'throw';
            else if (variant.type == 'muslin-throw') variant.modelId = 'muslin-throw';
            else if (variant.type == 'quilt') {
                variant.modelId = `quilt`;
            }

            // For standard pillows, format is "<type>_<index of variant within type>_<total standard>_<total european>"
            else if (variant.type.includes('pillow') && !variant.type.includes('european')) {
                variant.modelId = `${variant.type}_${variant.index}_${types[variant.type].length}_${types['european-pillowcase'] ? types['european-pillowcase'].length : 0}`;
            }

            // For other products, format is "<type>_<index of variant within type>_<total of type>"
            else variant.modelId = `${variant.type}_${variant.index}_${types[variant.type].length}`;
        }

        this.updateConfig(variants, { updateShadows, fireEvent });

        // Set floor shadow map
        this.shadow.material.alphaMap = variants[0].type == 'quilt' ? this.shadow.quiltAlpha : 
            variants[0].fabric == 'Cotton' ? this.shadow.duvetCottonAlpha : this.shadow.duvetLinenAlpha;
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (name == 'render-config' && newValue && newValue != oldValue) {
            this.config = JSON.parse(newValue);
            if (this.ready) {
                if (this.updateHandle) cancelAnimationFrame(this.updateHandle);
                this.updateHandle = requestAnimationFrame(() => this.loadConfig(this.config));
            }
        }
    }

    async generateShadowTexture() {
        // Dynamically import and initialize ProgressiveShadows if not already loaded
        if (!this.progressiveShadows) {
            const { ProgressiveShadows } = await import('./progressive-shadows.js');
            
            this.progressiveShadows = new ProgressiveShadows(this.renderer, this.scene, { 
                size: 5, 
                lightRadius: 4, 
                frames: 120, 
                lightCount: 8, 
                shadowMapRes: 1024, 
                ambientWeight: 0.6 
            });
            
            this.progressiveShadows.shadowCatcherMaterial.opacity = 1;
            this.progressiveShadows.lightOrigin.position.set(3.5, 3.5, 0);
            this.progressiveShadows.ambientWeight = 0.5;
            this.progressiveShadows.progShadowGrp.visible = false;
        }

        let ps = this.progressiveShadows;
        let canvas
        let uint8ClampedArray
        let color;

        ps.clear();
        ps.progShadowGrp.visible = true;

        this.scene.position.setY(0);
        ps.update(this.camera);
        this.scene.position.setY(-0.35);
        this.renderer.shadowMap.needsUpdate = true;

        ps.progShadowGrp.visible = false;

        if (!canvas) {
            canvas = document.createElement("canvas")
            color = new three.Color()
        }

        const renderTarget = ps.progressiveLightMap1
        const width = renderTarget.width
        const height = renderTarget.height
        console.log(renderTarget)
        const pixels = new Float32Array(width * height * 4)
        ps.renderer.readRenderTargetPixels(renderTarget, 0, 0, width, height, pixels)
        const shadowColor = ps.shadowCatcherMaterial.color
        const blend = ps.shadowCatcherMaterial.blend

        let min = 100000,
            max = 0
        for (let i = 0; i < pixels.length; i += 4) {
            min = Math.min(min, pixels[i])
            max = Math.max(max, pixels[i])
        }

        const range = max - min
        const alphaScale = 1 / range

        for (let i = 0; i < pixels.length; i += 4) {
            color.fromArray(pixels, i)
            const diffuse = color.r
            const invertedValue = max - diffuse
            const alphaValue = invertedValue * alphaScale
            pixels[i + 3] = 255;
            color.setRGB(255 * (alphaValue / 255), 255 * (alphaValue / 255), 255 * (alphaValue / 255))
            color.convertLinearToSRGB()
            color.toArray(pixels, i)
        }

        if (!uint8ClampedArray || uint8ClampedArray.length !== pixels.length) {
            uint8ClampedArray = new Uint8ClampedArray(pixels.length)
        }

        // Flip pixels vertically
        for (let y = 0; y < height / 2; y++) {
            for (let x = 0; x < width; x++) {
                const i = (y * width + x) * 4
                const j = ((height - y - 1) * width + x) * 4
                for (let k = 0; k < 4; k++) {
                    const temp = pixels[i + k]
                    pixels[i + k] = pixels[j + k]
                    pixels[j + k] = temp
                }
            }
        }

        for (let i = 0; i < pixels.length; i++) {
            uint8ClampedArray[i] = Math.round(pixels[i] * 255)
        }

        // setup canvas to draw the image data onto
        canvas.width = width
        canvas.height = height

        // Draw the image data onto the canvas
        const context = canvas.getContext("2d")
        const imageData = new ImageData(uint8ClampedArray, width, height)
        context.putImageData(imageData, 0, 0)

        // Create a data URL for the image
        const pngUrl = canvas.toDataURL("image/jpeg", 90);

        // Create a new anchor element
        const link = document.createElement("a")

        // Set the href attribute of the anchor element to the PNG data URL
        link.href = pngUrl

        // Set the download attribute of the anchor element to the desired file name
        const { type, fabric } = this.config.variants[0];
        link.download = `${type.split(' ')[0].toLowerCase()}-${fabric.toLowerCase()}-shadow-alpha.jpg`;

        // Simulate a click on the anchor element to download the image
        link.click();
    }
}

customElements.define('byob-render', ByobRender);